// Filter-state för screenern: typer, defaults, URL-konvertering.
// Hela state:t är serialiserbart så vi kan lagra det i URL:en (delbart).

export type SortField =
  | 'ticker'
  | 'name'
  | 'price'
  | 'pe_ratio'
  | 'dividend_yield'
  | 'market_cap'
  | 'rsi_14'
  | 'last_updated';

export type SortDir = 'asc' | 'desc' | null;

export interface RangeFilter {
  min: number | null; // null = öppen (ingen nedre gräns)
  max: number | null;
}

export interface ScreenerState {
  q: string;
  pe: RangeFilter;
  div: RangeFilter;
  mcap: RangeFilter;
  rsi: RangeFilter;
  sector: string;
  country: string;
  sort: { field: SortField; dir: SortDir };
  page: number;
  perPage: number;
}

// Rimliga default-spann baserat på fördelningen av nordiska+globala aktier.
// Vi sätter vida övre gränser så outliers (t.ex. AAPL/MSFT som ibland har
// skräp-yield-data efter en scraper-bugg) inte utesluts från default-vyn.
// Användaren kan alltid dra i sliders för att snäva in.
export const SCREENER_DEFAULTS: ScreenerState = {
  q: '',
  pe: { min: 0, max: 200 },
  div: { min: 0, max: 50 },
  mcap: { min: 0, max: 10_000_000_000_000 }, // 0–10 T
  rsi: { min: 0, max: 100 },
  sector: '',
  country: '',
  sort: { field: 'market_cap', dir: 'desc' },
  page: 1,
  perPage: 50,
};

// ---- URL <-> state ----

/** Plocka state från URLSearchParams med fall-back till default. */
export function stateFromSearchParams(params: URLSearchParams): ScreenerState {
  const num = (k: string): number | null => {
    const v = params.get(k);
    if (v === null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const text = (k: string): string => params.get(k) ?? '';

  const sortField = (params.get('sort') ?? 'market_cap') as SortField;
  const sortDirRaw = params.get('dir');
  const sortDir: SortDir = sortDirRaw === 'asc' || sortDirRaw === 'desc' ? sortDirRaw : null;

  return {
    q: text('q'),
    pe: { min: num('pe_min'), max: num('pe_max') },
    div: { min: num('div_min'), max: num('div_max') },
    mcap: { min: num('mcap_min'), max: num('mcap_max') },
    rsi: { min: num('rsi_min'), max: num('rsi_max') },
    sector: text('sector'),
    country: text('country'),
    sort: { field: sortField, dir: sortDir },
    page: num('page') ?? 1,
    perPage: num('per_page') ?? 50,
  };
}

/** Skriv state till URLSearchParams (utesluter default-värden för snygga URL:er). */
export function stateToSearchParams(s: ScreenerState): URLSearchParams {
  const out = new URLSearchParams();

  if (s.q) out.set('q', s.q);
  const setRange = (prefix: string, r: RangeFilter) => {
    if (r.min !== null) out.set(`${prefix}_min`, String(r.min));
    if (r.max !== null) out.set(`${prefix}_max`, String(r.max));
  };
  setRange('pe', s.pe);
  setRange('div', s.div);
  setRange('mcap', s.mcap);
  setRange('rsi', s.rsi);
  if (s.sector) out.set('sector', s.sector);
  if (s.country) out.set('country', s.country);
  if (s.sort.dir) {
    out.set('sort', s.sort.field);
    out.set('dir', s.sort.dir);
  }
  if (s.page !== 1) out.set('page', String(s.page));
  if (s.perPage !== 50) out.set('per_page', String(s.perPage));
  return out;
}

// ---- PocketBase-filter builder ----

/** Bygger en PocketBase-filtersträng från ScreenerState. Returnerar tom sträng om inga filter. */
export function buildPbFilter(s: ScreenerState): string {
  const clauses: string[] = [];
  if (s.q) {
    const safe = s.q.replace(/"/g, '""');
    clauses.push(`(ticker ~ "${safe}" || name ~ "${safe}")`);
  }
  const numRange = (field: string, r: RangeFilter) => {
    if (r.min !== null) clauses.push(`${field} >= ${r.min}`);
    if (r.max !== null) clauses.push(`${field} <= ${r.max}`);
  };
  numRange('pe_ratio', s.pe);
  numRange('dividend_yield', s.div);
  numRange('market_cap', s.mcap);
  numRange('rsi_14', s.rsi);
  if (s.sector) clauses.push(`sector = '${s.sector.replace(/'/g, "''")}'`);
  if (s.country) clauses.push(`country = '${s.country.replace(/'/g, "''")}'`);
  return clauses.join(' && ');
}

/** Bygger PocketBase-sortsträng (med `-` prefix för desc). Tom sträng = ingen sortering. */
export function buildPbSort(s: ScreenerState): string {
  if (!s.sort.dir) return '';
  return `${s.sort.dir === 'desc' ? '-' : ''}${s.sort.field}`;
}
